// src/pages/AdminPanel/SecurityMasterUploader.jsx
// Upload Security Master data (CSV) into fp_security_master and fp_security_master_valuations

import React, { useState } from "react";
import Papa from "papaparse";
import SectionCard from "components/SectionCard";
import { FileBrowseInput } from "components/InputStyled";
import { supabase } from "services/supabaseClient";
import { logAudit } from "lib/audit";
import { useProfile } from "context/ProfileContext";
import {
  toISO,
  toNullableFloat,
  cleanVendorText,
  parseBooleanFalseOnUnknown,
  chunk,
} from "lib/uploadHelpers";

const SECURITY_MASTER_BATCH_SIZE = 500;
const SECURITY_MASTER_REQUIRED_HEADERS = ["cusip"];

function validateSecurityMasterHeaders(parsedHeaders) {
  const headers = parsedHeaders.map((h) => String(h || "").toLowerCase().trim());
  return SECURITY_MASTER_REQUIRED_HEADERS.every((h) => headers.includes(h));
}

function normalizeCusip(v) {
  const t = cleanVendorText(v);
  return t ? t.toUpperCase().replace(/[-\s]/g, "") : null;
}

function toCsvDateOrNull(v) {
  const t = cleanVendorText(v);
  return t ? toISO(t) : null;
}

function normalizeSecurityMasterDayCountBasis(row, cusip) {
  const basis = cleanVendorText(row.day_count_basis);
  const securityType = cleanVendorText(row.security_type)?.toLowerCase() || "";
  const isTreasury = securityType.includes("treasury") || /^912/.test(cusip);

  return isTreasury ? "ACT/ACT-UST" : basis;
}

function buildSecurityMasterStaticRow(row) {
  const cusip = normalizeCusip(row.cusip);
  if (!cusip) return null;

  return {
    cusip,
    source_name: cleanVendorText(row.source_name),
    source_load_date: toCsvDateOrNull(row.source_load_date),

    issuer_name: cleanVendorText(row.issuer_name),
    security_description: cleanVendorText(row.security_description),
    security_type: cleanVendorText(row.security_type),
    asset_class: cleanVendorText(row.asset_class),
    sector: cleanVendorText(row.sector),
    subsector: cleanVendorText(row.subsector),
    ticker: cleanVendorText(row.ticker),

    currency: cleanVendorText(row.currency) || "USD",
    country: cleanVendorText(row.country),
    state: cleanVendorText(row.state),

    issue_date: toCsvDateOrNull(row.issue_date),
    dated_date: toCsvDateOrNull(row.dated_date),
    maturity_date: toCsvDateOrNull(row.maturity_date),
    first_interest_payment_date: toCsvDateOrNull(
      row.first_interest_payment_date
    ),
    first_call_date: toCsvDateOrNull(row.first_call_date),

    coupon: toNullableFloat(row.coupon),
    coupon_type: cleanVendorText(row.coupon_type),
    rate_type: cleanVendorText(row.rate_type),
    payment_frequency: cleanVendorText(row.payment_frequency),
    day_count_basis: normalizeSecurityMasterDayCountBasis(row, cusip),

    is_callable: parseBooleanFalseOnUnknown(row.is_callable),
    is_puttable: parseBooleanFalseOnUnknown(row.is_puttable),
    maturity_option_type: cleanVendorText(row.maturity_option_type),
    sinkable_flag: parseBooleanFalseOnUnknown(row.sinkable_flag),
    insured_flag: parseBooleanFalseOnUnknown(row.insured_flag),
    taxable_flag: parseBooleanFalseOnUnknown(row.taxable_flag),

    collateral_type: cleanVendorText(row.collateral_type),
    guarantor: cleanVendorText(row.guarantor),
    benchmark_sector: cleanVendorText(row.benchmark_sector),

    metadata: {
      upload_source: "admin_csv",
      original_row_snapshot: {
        source_name: row.source_name ?? null,
        source_load_date: row.source_load_date ?? null,
      },
    },

    loaded_at: new Date().toISOString(),
    last_verified_at: new Date().toISOString(),
    is_active: true,
  };
}

function buildSecurityMasterValuationSeed(row) {
  const cusip = normalizeCusip(row.cusip);
  if (!cusip) return null;

  const explicitValuationDate = toCsvDateOrNull(row.valuation_date);
  const valuationDate = explicitValuationDate || toCsvDateOrNull(row.source_load_date);
  if (!valuationDate) return null;

  return {
    cusip,
    valuation_date: valuationDate,
    source_name: cleanVendorText(row.source_name),
    source_load_date: toCsvDateOrNull(row.source_load_date),
    loaded_at: new Date().toISOString(),

    next_call_date: toCsvDateOrNull(row.next_call_date),
    escrowed_flag: parseBooleanFalseOnUnknown(row.escrowed_flag),

    rating_moody: cleanVendorText(row.rating_moody),
    rating_sp: cleanVendorText(row.rating_sp),
    rating_fitch: cleanVendorText(row.rating_fitch),
    rating_rollup: cleanVendorText(row.rating_rollup),

    yield_ytm_mid: toNullableFloat(row.yield_ytm_mid),
    yield_ytc_mid: toNullableFloat(row.yield_ytc_mid),
    price_mid: toNullableFloat(row.price_mid),

    metadata: {
      upload_source: "admin_csv",
      original_row_snapshot: {
        source_name: row.source_name ?? null,
        source_load_date: row.source_load_date ?? null,
        valuation_date: row.valuation_date ?? null,
      },
    },
  };
}

export default function SecurityMasterUploader() {
  const { profile, loading: profileLoading } = useProfile();

  const isPlatformAdmin = !!profile?.is_admin;
  const userId = profile?.id ?? null;

  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState([]);

  if (profileLoading) {
    return <div className="p-4 text-muted">Loading…</div>;
  }

  if (!isPlatformAdmin) {
    return <div className="p-4 text-app">Platform admin access required.</div>;
  }

  const handleFileChange = (selectedFile) => {
    setFile(selectedFile);
    setStatus("");
    setPreview([]);
  };

  const parseAndUpload = async () => {
    if (!file) return setStatus("❌ Choose a file first.");
    if (!userId) return setStatus("❌ You must be signed in.");

    const auditAction = "security_master_upload";
    let batchId = null;

    setStatus("Parsing…");

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: ",",
        complete: async ({ data, errors, meta }) => {
          try {
            if (errors?.length) {
              setStatus(`❌ CSV parse error: ${errors[0].message}`);
              return;
            }

            if (!data?.length) {
              setStatus("❌ No rows found in CSV.");
              return;
            }

            const parsedHeaders = meta.fields || Object.keys(data[0] || {});
            if (!validateSecurityMasterHeaders(parsedHeaders)) {
              setStatus(
                '❌ This file does not look like a valid Security Master CSV. Required header: "cusip".'
              );
              return;
            }

            const staticRowsRaw = data
              .map((row) => buildSecurityMasterStaticRow(row))
              .filter(Boolean);

            const valuationSeedsRaw = data
              .map((row) => buildSecurityMasterValuationSeed(row))
              .filter(Boolean);

            if (!staticRowsRaw.length) {
              setStatus("❌ No usable rows found.");
              return;
            }

            const duplicateCounts = new Map();
            for (const row of staticRowsRaw) {
              duplicateCounts.set(row.cusip, (duplicateCounts.get(row.cusip) || 0) + 1);
            }

            const duplicateCusips = [...duplicateCounts.entries()]
              .filter(([, count]) => count > 1)
              .map(([cusip]) => cusip);

            const staticRowMap = new Map();
            for (const row of staticRowsRaw) staticRowMap.set(row.cusip, row);
            const staticRowsDeduped = Array.from(staticRowMap.values());

            // valuation rows are unique by (cusip, valuation_date), last occurrence wins
            const valuationSeedMap = new Map();
            for (const row of valuationSeedsRaw) {
              valuationSeedMap.set(`${row.cusip}__${row.valuation_date}`, row);
            }
            const valuationSeedsDeduped = Array.from(valuationSeedMap.values());

            const uniqueSourceNames = Array.from(
              new Set(staticRowsDeduped.map((r) => r.source_name).filter(Boolean))
            );

            const uniqueSourceLoadDates = Array.from(
              new Set(staticRowsDeduped.map((r) => r.source_load_date).filter(Boolean))
            ).sort();

            const vendorName =
              uniqueSourceNames.length === 1
                ? uniqueSourceNames[0]
                : uniqueSourceNames.length > 1
                ? "Mixed"
                : null;

            const fileDate =
              uniqueSourceLoadDates.length === 1
                ? uniqueSourceLoadDates[0]
                : uniqueSourceLoadDates.length
                ? uniqueSourceLoadDates[uniqueSourceLoadDates.length - 1]
                : null;

            setPreview(staticRowsDeduped.slice(0, 5));
            setStatus(
              `Creating inbound batch for ${staticRowsDeduped.length} security row(s)…`
            );

            const payload = {
              batch_type: "inbound_response",
              vendor_name: vendorName,
              file_name: file.name,
              file_date: fileDate,
              status: "received",
              record_count: staticRowsDeduped.length,
              created_by: userId,
              received_at: new Date().toISOString(),
              notes:
                duplicateCusips.length > 0
                  ? `Duplicate CUSIPs in file were deduped by last occurrence: ${duplicateCusips
                      .slice(0, 25)
                      .join(", ")}${duplicateCusips.length > 25 ? " …" : ""}`
                  : null,
            };

            const { data: batchRow, error: batchCreateError } = await supabase
              .from("fp_security_master_batches")
              .insert(payload)
              .select("id")
              .single();

            if (batchCreateError) {
              console.error("Security Master batch create error:", batchCreateError);
              throw new Error(
                `Batch create: ${
                  batchCreateError?.message ||
                  batchCreateError?.details ||
                  batchCreateError?.hint ||
                  JSON.stringify(batchCreateError)
                }`
              );
            }

            batchId = batchRow.id;

            const staticRowsWithBatchMetadata = staticRowsDeduped.map((row, idx) => ({
              ...row,
              metadata: {
                ...(row.metadata || {}),
                batch_id: batchId,
                file_name: file.name,
                file_row_number: idx + 2,
              },
            }));

            setStatus(
              `Upserting ${staticRowsWithBatchMetadata.length} static security master row(s)…`
            );

            for (const batch of chunk(staticRowsWithBatchMetadata, SECURITY_MASTER_BATCH_SIZE)) {
              const { error } = await supabase.from("fp_security_master").upsert(batch, {
                onConflict: "cusip",
              });
              if (error) throw new Error(`fp_security_master upsert: ${error.message}`);
            }

            setStatus("Resolving security master IDs for valuation import…");

            const cusips = staticRowsWithBatchMetadata.map((r) => r.cusip);
            const securityMasterRows = [];

            for (const batch of chunk(cusips, SECURITY_MASTER_BATCH_SIZE)) {
              const { data: foundRows, error } = await supabase
                .from("fp_security_master")
                .select("id, cusip")
                .in("cusip", batch);

              if (error) {
                throw new Error(`fp_security_master lookup: ${error.message}`);
              }

              securityMasterRows.push(...(foundRows || []));
            }

            const securityMasterIdByCusip = new Map(
              securityMasterRows.map((row) => [normalizeCusip(row.cusip), row.id])
            );

            const valuationRows = valuationSeedsDeduped
              .map((row, idx) => {
                const securityMasterId = securityMasterIdByCusip.get(row.cusip);
                if (!securityMasterId) return null;

                return {
                  security_master_id: securityMasterId,
                  valuation_date: row.valuation_date,
                  source_name: row.source_name,
                  source_load_date: row.source_load_date,
                  loaded_at: row.loaded_at,
                  next_call_date: row.next_call_date,
                  escrowed_flag: row.escrowed_flag,
                  rating_moody: row.rating_moody,
                  rating_sp: row.rating_sp,
                  rating_fitch: row.rating_fitch,
                  rating_rollup: row.rating_rollup,
                  yield_ytm_mid: row.yield_ytm_mid,
                  yield_ytc_mid: row.yield_ytc_mid,
                  price_mid: row.price_mid,
                  metadata: {
                    ...(row.metadata || {}),
                    batch_id: batchId,
                    file_name: file.name,
                    file_row_number: idx + 2,
                    cusip: row.cusip,
                  },
                };
              })
              .filter(Boolean);

            setStatus(`Upserting ${valuationRows.length} valuation row(s)…`);

            for (const batch of chunk(valuationRows, SECURITY_MASTER_BATCH_SIZE)) {
              const { error } = await supabase
                .from("fp_security_master_valuations")
                .upsert(batch, {
                  onConflict: "security_master_id,valuation_date",
                });

              if (error) {
                throw new Error(`fp_security_master_valuations upsert: ${error.message}`);
              }
            }

            const { error: batchProcessedError } = await supabase
              .from("fp_security_master_batches")
              .update({
                status: "processed",
                processed_at: new Date().toISOString(),
                record_count: staticRowsWithBatchMetadata.length,
              })
              .eq("id", batchId);

            if (batchProcessedError) {
              throw new Error(`Batch finalize: ${batchProcessedError.message}`);
            }

            await logAudit({
              category: "upload",
              action: auditAction,
              success: true,
              detail: {
                fileName: file.name,
                batchId,
                rowsParsed: data.length,
                staticRowsImported: staticRowsWithBatchMetadata.length,
                valuationRowsImported: valuationRows.length,
                duplicateCusips: duplicateCusips.length,
                vendorName,
                fileDate,
              },
            });

            setStatus(
              `✅ Upload complete.\n` +
                `Batch ID: ${batchId}\n` +
                `Static rows imported: ${staticRowsWithBatchMetadata.length}\n` +
                `Valuation rows imported: ${valuationRows.length}` +
                (duplicateCusips.length
                  ? `\n⚠️ Duplicate CUSIPs deduped by last occurrence: ${duplicateCusips.length}`
                  : "")
            );
          } catch (e) {
            if (batchId) {
              await supabase
                .from("fp_security_master_batches")
                .update({
                  status: "failed",
                  failed_at: new Date().toISOString(),
                  notes: e.message,
                })
                .eq("id", batchId);
            }

            await logAudit({
              category: "upload",
              action: auditAction,
              success: false,
              detail: {
                fileName: file?.name,
                batchId,
                error: e.message,
              },
            });

            setStatus(`❌ Upload error: ${e.message}`);
          }
        },
        error: async (err) => {
          await logAudit({
            category: "upload",
            action: auditAction,
            success: false,
            detail: {
              fileName: file?.name,
              error: err.message,
            },
          });
          setStatus(`❌ CSV parse error: ${err.message}`);
        },
      });
    } catch (e) {
      if (batchId) {
        await supabase
          .from("fp_security_master_batches")
          .update({
            status: "failed",
            failed_at: new Date().toISOString(),
            notes: e.message,
          })
          .eq("id", batchId);
      }

      await logAudit({
        category: "upload",
        action: auditAction,
        success: false,
        detail: {
          fileName: file?.name,
          batchId,
          error: e.message,
        },
      });

      setStatus(`❌ Upload error: ${e.message}`);
    }
  };

  return (
    <SectionCard title="Upload Security Master Data">
      <div className="flex flex-wrap items-end gap-3">
        <FileBrowseInput
          label="Upload File"
          file={file}
          accept=".csv"
          placeholder="Select a CSV file"
          onFileChange={handleFileChange}
          className="min-w-96"
        />

      </div>

      <button
        onClick={parseAndUpload}
        className="btn btn-primary mt-3 mb-4 disabled:opacity-50"
        disabled={!file}
      >
        Upload
      </button>

      {status && <p className="text-sm whitespace-pre-line mb-3">{status}</p>}

      {preview.length > 0 && (
        <div className="text-xs text-muted">
          <p className="font-semibold">Preview:</p>
          <pre className="overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(preview, null, 2)}
          </pre>
        </div>
      )}

      <p className="text-xs text-muted">
        Notes: static fields are upserted into <span className="font-semibold">fp_security_master</span>{" "}
        by <span className="font-semibold">cusip</span>. Valuation snapshot fields are written to{" "}
        <span className="font-semibold">fp_security_master_valuations</span> using an explicit{" "}
        <span className="font-semibold">valuation_date</span> column when present, otherwise{" "}
        <span className="font-semibold">source_load_date</span>. Unknown booleans are forced to false.
      </p>
    </SectionCard>
  );
}
